/**
 * Consolidated style and color utility functions for Driver Debug view.
 * Separates styling concerns from data composition logic.
 */
import { ApiAnnotationDriverDay } from '@/rennovation/planning-view/types/api/roster-response';
import type { DayEvaluation } from '@/rennovation/planning-view/types/api/roster-response';

export function useDriverDebugStyles() {
	function getDayHeaderStyle(status: string): Record<string, string> {
		const map: Record<string, string> = {
			open: 'var(--ddd-status-open)',
			planning: 'var(--ddd-status-planning)',
			published: 'var(--ddd-status-published)',
			past: 'var(--ddd-status-open)',
			locked: 'var(--ddd-status-locked)',
			preview: 'var(--ddd-status-preview)',
		};
		return { backgroundColor: map[status] ?? 'var(--ddd-bg-card)' };
	}

	function getDutyCellStyle(type: string, isRestSunday: boolean): Record<string, string> {
		if (type === 'rest') {
			return { backgroundColor: isRestSunday ? 'var(--ddd-duty-rest-sunday)' : 'var(--ddd-duty-rest)' };
		}
		const map: Record<string, string> = {
			duty: 'var(--ddd-duty-duty)',
			reserve: 'var(--ddd-duty-reserve)',
			compensation: 'var(--ddd-duty-compensation)',
			vacation: 'var(--ddd-duty-vacation)',
			absence: 'var(--ddd-duty-absence)',
		};
		return { backgroundColor: map[type] ?? 'var(--ddd-bg-card)' };
	}

	function getDutyCellStyleForDay(day: ApiAnnotationDriverDay): Record<string, string> {
		const duty = day?.duty;
		if (!duty?.type || typeof duty.isRestSunday === 'undefined') {
			return { backgroundColor: 'var(--ddd-bg-card)' };
		}
		return getDutyCellStyle(duty.type, duty.isRestSunday);
	}

	function getShiftBackgroundColor(shift: string | number): string {
		const map: Record<string | number, string> = {
			1: 'var(--ddd-shift-1)',
			2: 'var(--ddd-shift-2)',
			3: 'var(--ddd-shift-3)',
			4: 'var(--ddd-shift-4)',
		};
		return map[shift] ?? 'var(--ddd-bg-card)';
	}

	function getShiftCellStyle(day: ApiAnnotationDriverDay): Record<string, string> {
		if (day?.shift == null) return { backgroundColor: 'var(--ddd-bg-card)' };
		return { backgroundColor: getShiftBackgroundColor(day.shift) };
	}

	function getStandbyCellStyle(day: ApiAnnotationDriverDay): Record<string, string> {
		return day?.isStandby ? { backgroundColor: 'var(--ddd-standby-bg)' } : { backgroundColor: 'var(--ddd-bg-card)' };
	}

	function getMetCellStyle(m: DayEvaluation): Record<string, string> {
		if (!m?.total) return { backgroundColor: 'var(--ddd-bg-card)' };
		return { backgroundColor: 'var(--ddd-met-bg)' };
	}

	function getUnmetCellStyle(u: DayEvaluation): Record<string, string> {
		if (!u?.total) return { backgroundColor: 'var(--ddd-bg-card)' };
		return { backgroundColor: 'var(--ddd-unmet-bg)' };
	}

	function getViolCellStyle(v: DayEvaluation): Record<string, string> {
		if (!v?.total) return { backgroundColor: 'var(--ddd-bg-card)' };
		return { backgroundColor: 'var(--ddd-viol-bg)' };
	}

	return {
		getDayHeaderStyle,
		getDutyCellStyleForDay,
		getShiftCellStyle,
		getStandbyCellStyle,
		getMetCellStyle,
		getUnmetCellStyle,
		getViolCellStyle,
	};
}
